import { Button, DialogContentText } from "@material-ui/core";
// import IconButton from "@material-ui/core/IconButton";
// import DeleteIcon from "@material-ui/icons/Delete";
import ActionDialog from "@saleor/components/ActionDialog";
import DeleteFilterTabDialog from "@saleor/components/DeleteFilterTabDialog";
import SaveFilterTabDialog, {
  SaveFilterTabDialogFormData
} from "@saleor/components/SaveFilterTabDialog";
// import { PAGINATE_BY } from "@saleor/config";
import { configurationMenuUrl } from "@saleor/configuration";
import CountryBulkDeleteDialog from "@saleor/countries/components/CountryBulkDeleteDialog/CountryBulkDeleteDialog";
import CountryListPage from "@saleor/countries/components/CountryListPage";
import {
  useCountryBulkDeleteMutation,
  useCountryBulkEnableMutation
} from "@saleor/countries/mutations";
import { useCountryListQuery } from "@saleor/countries/queries";
import {
  countryAddUrl,
  countryListUrl,
  CountryListUrlDialog,
  CountryListUrlQueryParams,
  countryUrl
} from "@saleor/countries/urls";
import useBulkActions from "@saleor/hooks/useBulkActions";
import useListSettings from "@saleor/hooks/useListSettings";
import useNavigator from "@saleor/hooks/useNavigator";
import useNotifier from "@saleor/hooks/useNotifier";
import usePaginator, {
  createPaginationState
} from "@saleor/hooks/usePaginator";
import useShop from "@saleor/hooks/useShop";
import { commonMessages } from "@saleor/intl";
import { maybe } from "@saleor/misc";
import { ListViews } from "@saleor/types";
import createDialogActionHandlers from "@saleor/utils/handlers/dialogActionHandlers";
import createFilterHandlers from "@saleor/utils/handlers/filterHandlers";
import createSortHandler from "@saleor/utils/handlers/sortHandler";
import { getSortParams } from "@saleor/utils/sort";
import React from "react";
import { FormattedMessage, useIntl } from "react-intl";

import {
  areFiltersApplied,
  deleteFilterTab,
  getActiveFilters,
  getFilterOpts,
  getFilterQueryParam,
  getFilterTabs,
  getFilterVariables,
  saveFilterTab
} from "./filters";
import { getSortQueryVariables } from "./sort";

interface CountryListProps {
  params: CountryListUrlQueryParams;
}

const CountryList: React.FC<CountryListProps> = ({ params }) => {
  const navigate = useNavigator();
  const paginate = usePaginator();
  const notify = useNotifier();
  const shop = useShop();
  const { isSelected, listElements, reset, toggle, toggleAll } = useBulkActions(
    params.ids
  );
  const intl = useIntl();

  const { updateListSettings, settings } = useListSettings(
    ListViews.COUNTRIES_LIST
  );
  // const paginationState = createPaginationState(PAGINATE_BY, params);
  const paginationState = createPaginationState(settings.rowNumber, params);

  // const queryVariables = React.useMemo(
  //   () => ({
  //     ...paginationState,
  //     filter: getFilterVariables(params),
  //     sort: getSortQueryVariables(params)
  //   }),
  //   [params]
  // );
  const queryVariables = {
    ...paginationState,
    filter: getFilterVariables(params),
    sort: getSortQueryVariables(params)
  };
  const tabs = getFilterTabs();

  const { data, loading, refetch } = useCountryListQuery({
    variables: queryVariables
  });

  const currentTab =
    params.activeTab === undefined
      ? areFiltersApplied(params)
        ? tabs.length + 1
        : 0
      : parseInt(params.activeTab, 0);
  const [openModal, closeModal] = createDialogActionHandlers<
    CountryListUrlDialog,
    CountryListUrlQueryParams
  >(navigate, countryListUrl, params);
  const [
    changeFilters,
    resetFilters,
    handleSearchChange
  ] = createFilterHandlers({
    cleanupFn: reset,
    createUrl: countryListUrl,
    getFilterQueryParam,
    navigate,
    params
  });
  const handleTabChange = (tab: number) => {
    reset();
    navigate(
      countryListUrl({
        activeTab: tab.toString(),
        ...getFilterTabs()[tab - 1].data
      })
    );
  };

  const handleTabDelete = () => {
    deleteFilterTab(currentTab);
    reset();
    navigate(countryListUrl());
  };

  const handleTabSave = (data: SaveFilterTabDialogFormData) => {
    saveFilterTab(data.name, getActiveFilters(params));
    handleTabChange(tabs.length + 1);
  };
  const { loadNextPage, loadPreviousPage, pageInfo } = paginate(
    maybe(() => data.countries.pageInfo),
    paginationState,
    params
  );
  const handleSort = createSortHandler(navigate, countryListUrl, params);
  const currencySymbol = maybe(() => shop.defaultCurrency, "USD");

  const [
    countryBulkDelete,
    countryBulkDeleteOpts
  ] = useCountryBulkDeleteMutation({
    onCompleted: data => {
      if (data.countryBulkDelete.errors.length === 0) {
        closeModal();
        notify({
          status: "success",
          text: intl.formatMessage({
            defaultMessage: "Country successfully delete",
            description: "deleted multiple Country"
          })
        });
        reset();
        refetch();
      }
    }
  });

  const [
    countryBulkEnable,
    countryBulkEnableOpts
  ] = useCountryBulkEnableMutation({
    onCompleted: data => {
      if (data.countryBulkEnable.errors.length === 0) {
        notify({
          status: "success",
          text: intl.formatMessage(commonMessages.savedChanges)
        });
        refetch();
        closeModal();
      }
    }
  });
  return (
    <>
      <CountryListPage
        toatlCount = {maybe(() => data.countries.totalCount,0)}
        country={maybe(() => data.countries.edges.map(edge => edge.node))}
        currencySymbol={currencySymbol}
        currentTab={currentTab}
        disabled={loading || countryBulkDeleteOpts.loading}
        filterOpts={getFilterOpts(params)}
        initialSearch={params.query || ""}
        isChecked={isSelected}
        onAdd={() => navigate(countryAddUrl())}
        onAll={resetFilters}
        onBack={() => navigate(configurationMenuUrl)}
        onFilterChange={changeFilters}
        onNextPage={loadNextPage}
        onPreviousPage={loadPreviousPage}
        onRowClick={id => () => navigate(countryUrl(id))}
        onSearchChange={handleSearchChange}
        onSort={handleSort}
        onTabChange={handleTabChange}
        onTabDelete={() => openModal("delete-search")}
        onTabSave={() => openModal("save-search")}
        pageInfo={pageInfo}
        selected={listElements.length}
        sort={getSortParams(params)}
        settings={settings}
        onUpdateListSettings={updateListSettings}
        tabs={tabs.map(tab => tab.name)}
        toggle={toggle}
        toggleAll={toggleAll}
        toolbar={
          <>
            <Button
              color="primary"
              onClick={() =>
                openModal("enable", {
                  ids: listElements
                })
              }
            >
              <FormattedMessage
                defaultMessage="enable"
                description="enable country"
              />
            </Button>
            <Button
              color="primary"
              onClick={() =>
                openModal("unenable", {
                  ids: listElements
                })
              }
            >
              <FormattedMessage
                defaultMessage="unenable"
                description="unenable country"
              />
            </Button>
            {/* <IconButton
                  color="primary"
                  onClick={() =>
                    openModal("remove", {
                      ids: listElements
                    })
                  }
                >
                  <DeleteIcon />
                </IconButton> */}
          </>
        }
      />
      <CountryBulkDeleteDialog
        confirmButtonState={countryBulkDeleteOpts.status}
        open={params.action === "remove" && maybe(() => params.ids.length > 0)}
        onConfirm={() => countryBulkDelete({ variables: { ids: params.ids } })}
        onClose={closeModal}
        quantity={maybe(() => params.ids.length)}
      />
      <SaveFilterTabDialog
        open={params.action === "save-search"}
        confirmButtonState="default"
        onClose={closeModal}
        onSubmit={handleTabSave}
      />
      <DeleteFilterTabDialog
        open={params.action === "delete-search"}
        confirmButtonState="default"
        onClose={closeModal}
        onSubmit={handleTabDelete}
        tabName={maybe(() => tabs[currentTab - 1].name, "...")}
      />
      <ActionDialog
        open={
          params.action === "unenable" && maybe(() => params.ids.length > 0)
        }
        onClose={closeModal}
        confirmButtonState={countryBulkEnableOpts.status}
        onConfirm={() =>
          countryBulkEnable({
            variables: {
              ids: params.ids,
              enable: false
            }
          })
        }
        variant="default"
        title={intl.formatMessage({
          defaultMessage: "Unpublish collections",
          description: "dialog title"
        })}
      >
        <DialogContentText>
          <FormattedMessage
            defaultMessage="Are you sure you need to change the status of these countries?"
            values={{
              counter: maybe(() => params.ids.length),
              displayQuantity: <strong>{maybe(() => params.ids.length)}</strong>
            }}
          />
        </DialogContentText>
      </ActionDialog>
      <ActionDialog
        open={params.action === "enable" && maybe(() => params.ids.length > 0)}
        onClose={closeModal}
        confirmButtonState={countryBulkEnableOpts.status}
        onConfirm={() =>
          countryBulkEnable({
            variables: {
              ids: params.ids,
              enable: true
            }
          })
        }
        variant="default"
        title={intl.formatMessage({
          defaultMessage: "Unpublish collections",
          description: "dialog title"
        })}
      >
        <DialogContentText>
          <FormattedMessage
            defaultMessage="Are you sure you need to change the status of these countries?"
            values={{
              counter: maybe(() => params.ids.length),
              displayQuantity: <strong>{maybe(() => params.ids.length)}</strong>
            }}
          />
        </DialogContentText>
      </ActionDialog>
    </>
  );
};
export default CountryList;
